/**
 * Confirm subir a revisão de um tour.
 *
 * Asks because the action is one click away from a card and cannot be undone by clicking again:
 * bumping re-opens the tour for **everyone**, including people who had already been through it,
 * and the reach count restarts from zero. Nothing is deleted — the previous revision's records
 * stay — but the number people look at changes, and so does what every user sees on their next
 * visit.
 *
 * @module Pages/BUs/Tours
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { Tour } from '../../Types/Tours.types';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const versionRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-m);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-family: var(--font-mono);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
`;

const fromStyle = css`
  color: var(--color-text-muted);
`;

const alertStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  border: 1px solid var(--color-warning);
`;

const alertTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const alertTextStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface BumpTourModalProps {
  open: boolean;
  tour: Tour;
  /** Distinct viewers of the current revision — what the bump resets to zero. */
  viewCount?: number;
  isBumping: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (tour: Tour) => void;
}

export const BumpTourModal = ({
  open,
  tour,
  viewCount,
  isBumping,
  error,
  onClose,
  onConfirm,
}: BumpTourModalProps): JSX.Element => {
  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        O tour da página <strong>{tour.pageSlug}</strong> em <strong>{tour.tenantSlug}</strong> sobe
        de revisão.
      </p>

      <div className={versionRowStyle}>
        <span className={fromStyle}>v{tour.version}</span>
        <span>→</span>
        <span>v{tour.version + 1}</span>
      </div>

      <div className={alertStyle}>
        <span className={alertTitleStyle}>Todo mundo vai ver o tour de novo</span>
        <span className={alertTextStyle}>
          Inclusive quem já tinha passado por ele. É isso que subir a revisão faz: o tour volta a
          abrir sozinho para todos os usuários da plataforma no próximo acesso.
          {viewCount !== undefined && viewCount > 0
            ? ` A contagem de alcance recomeça do zero — as ${viewCount.toLocaleString('pt-BR')} visualizações da v${tour.version} passam a ser histórico da revisão anterior.`
            : ' A contagem de alcance recomeça do zero.'}
        </span>
      </div>

      {error !== null ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='secondary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='tour-bump-confirm'
          text={isBumping ? 'Subindo...' : `Subir para v${tour.version + 1}`}
          icon='Arrows.ArrowsClockwise'
          disabled={isBumping}
          onClick={() => {
            onConfirm(tour);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Subir revisão do tour'
      content={content}
      maxWidth='520px'
      hasButtonClose
      position='center'
    />
  );
};
